import { useState } from 'react'
import { Button as TamaguiButton, Dialog, useEvent, View } from 'tamagui'
import Close from '@/src/assets/icons/Close'
import { ScheduleCallForm } from './ScheduleCallForm'
import { InternationalPhoneNumber } from '@acre-rewards/lib'
import { useLogError } from '@/src/common/hooks/useLogError'
import { z } from 'zod/v4'
import { DesignSystem } from '@/src/design-system'
import { Button } from '@/src/common/components/Button'
import * as E2E from '../../e2e'
import * as Stores from '../../stores'
import { useSegmentDomain } from '@/src/services/segment/SegmentDomainProvider'

export function ScheduleCallButton({ stateCode }: { stateCode?: string }) {
  const logError = useLogError()
  const { triggerAction } = useSegmentDomain()
  const {
    data: scheduledCallData,
    isLoading: isScheduledCallDataLoading,
    refetch: refetchScheduledCallData,
  } = Stores.MortgageApi.endpoints['GET /mortgage/leads/scheduled-calls'].useQuery({})
  const [scheduleCall, { isLoading: isSchedulingCall }] =
    Stores.MortgageApi.endpoints['POST /mortgage/leads/scheduled-calls'].useMutation()
  const [open, setOpen] = useState(false)
  const onOpenChange = useEvent((open: boolean) => {
    setOpen(open)
    void triggerAction('ScheduleCallForm', open ? 'Opened' : 'Closed')
  })
  DesignSystem.ScreenWideReload.useReloadListener(() => void refetchScheduledCallData())

  const handleFormSubmit = useEvent(
    async ({ phoneNumber, timeOfDay }: { phoneNumber: string; timeOfDay: string }) => {
      try {
        const parsedPhoneNumber = InternationalPhoneNumber.safeParse(phoneNumber)

        if (!parsedPhoneNumber.success) {
          throw new Error('Invalid phone number format for Schedule Call Form', {
            cause: z.prettifyError(parsedPhoneNumber.error),
          })
        }

        const result = await scheduleCall({
          body: {
            phoneNumber: parsedPhoneNumber.data,
            bestTimeToCall: timeOfDay,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            stateCode,
          },
        })

        void triggerAction('ScheduleCallForm', 'Submitted')

        setOpen(false)

        if (result.error) {
          throw new Error('Failed to schedule call for Mesa Mortgage', { cause: result.error })
        }
      } catch (error) {
        logError(error)
      }
    },
  )

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild>
        <Button
          testID={E2E.Config.Ids.Press.ScheduleCall}
          size="sm"
          title={scheduledCallData?.hasCallScheduled ? 'Call Scheduled' : 'Schedule a Call'}
          loading={isScheduledCallDataLoading || isSchedulingCall}
          disabled={scheduledCallData?.hasCallScheduled}
        />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" style={{ backgroundColor: 'black', opacity: 0.5 }} />
        <Dialog.Content key="content" p="$4" gap="$4" pb="$8">
          <View testID={E2E.Config.Ids.View.ScheduleCallForm}>
            <ScheduleCallForm onFormSubmit={handleFormSubmit} />
          </View>
          <Dialog.Close asChild>
            <TamaguiButton
              r="$2"
              t="$2"
              position="absolute"
              size="$2"
              circular
              icon={<Close width={16} height={16} />}
              testID={E2E.Config.Ids.Press.ScheduleCallClose}
            />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Adapt when="maxMd" platform="touch">
        <Dialog.Sheet
          animation="medium"
          zIndex={200000}
          dismissOnSnapToBottom
          dismissOnOverlayPress
          modal
          snapPointsMode="fit"
          moveOnKeyboardChange
        >
          <Dialog.Sheet.Handle />
          <Dialog.Sheet.Frame p="$4" gap="$4" pb="$8">
            <Dialog.Adapt.Contents />
          </Dialog.Sheet.Frame>
          <Dialog.Sheet.Overlay style={{ backgroundColor: 'black', opacity: 0.5 }} />
        </Dialog.Sheet>
      </Dialog.Adapt>
    </Dialog>
  )
}
